import http from '../api/client';

export type RecipeTraceItem = {
  kind: string;
  name: string;
  detail: string;
  where: string;
  id?: string | number | null;
};

export type RecipeTraceEvent = {
  ts: string;
  traceId: string | null;
  recipeId: string | null;
  userId: string | number | null;
  userRole: string | null;
  stage: string;
  action: string | null;
  summary: {
    title: string | null;
    status: string | null;
    counts: {
      preparations: number;
      ingredients: number;
      medicines: number;
      steps: number;
    };
    items: RecipeTraceItem[];
    scalars: Record<string, string | null>;
    lists: Record<string, string[]>;
  } | null;
  dropped: string[];
  changed: string[];
  appeared: string[];
  notPersisted: string[];
  note: string | null;
  full: unknown;
};

export type RecipeTraceLogFiles = {
  directory: string;
  jsonl: string;
  text: string;
};

const openTraces = new Map<string, { traceId: string; at: number }>();

export function noteOpenTrace(recipeId: string | number, traceId: string) {
  openTraces.set(String(recipeId), { traceId, at: Date.now() });
}

export function recentOpenTrace(recipeId: string | number | null | undefined) {
  if (recipeId == null || recipeId === '') return null;
  const row = openTraces.get(String(recipeId));
  if (!row || Date.now() - row.at > 120000) return null;
  return row.traceId;
}

export function newRecipeTraceId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `trace-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function snapshotForTrace(value: unknown): unknown {
  const seen = new WeakSet<object>();
  try {
    return JSON.parse(
      JSON.stringify(value, (key, entry) => {
        if (key === 'data_trace_id' || key === 'data_trace_action')
          return undefined;
        if (typeof File !== 'undefined' && entry instanceof File) {
          return { _file: entry.name, size: entry.size, type: entry.type };
        }
        if (entry && typeof entry === 'object') {
          if (seen.has(entry)) return '[circular]';
          seen.add(entry);
        }
        return entry;
      }),
    );
  } catch {
    return { _unserializable: true };
  }
}

export async function recordRecipeTrace(body: {
  traceId?: string | null;
  recipeId?: string | number | null;
  action?: string | null;
  note?: string | null;
  stage?: string;
  snapshot?: unknown;
  stages?: Array<{ stage: string; snapshot?: unknown; note?: string | null }>;
}): Promise<void> {
  try {
    await http.post('/recipes/data-trace', body);
  } catch (error) {
    console.warn('[recipe-trace] failed to record', error);
  }
}

export async function fetchRecipeTraces(params: {
  recipeId?: string | number | null;
  limit?: number;
}): Promise<{
  events: RecipeTraceEvent[];
  logFiles: RecipeTraceLogFiles | null;
}> {
  const limit = params.limit ?? 120;
  const response = params.recipeId
    ? await http.get(`/recipes/${params.recipeId}/data-trace`, {
        params: { limit },
      })
    : await http.get('/recipes/data-trace', { params: { limit } });
  return {
    events: response.data?.data || [],
    logFiles: response.data?.logFiles || null,
  };
}
